import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { searchEntries, type SearchHit, type SearchOutcome } from '../api'
import { formatBytes, formatDate, parentPath } from '../format'
import { FileIcon, FolderIcon } from './icons'

/**
 * 이름으로 찾은 것들.
 *
 * 색인이 없으므로 이것은 디스크를 걷는 일이고, 걷는 동안 찾은 것이 한 건씩 흘러나온다.
 * 그래서 목록이 자라는 모습이 그대로 보인다 — 큰 폴더에서 몇 초를 빈 화면으로 두는
 * 것보다, 첫 줄이 곧 뜨는 쪽이 기다릴 만하다.
 */
export default function SearchResults({
  rootId,
  path,
  query,
}: {
  rootId: string
  /** 이 폴더 밑에서만 찾는다. */
  path: string
  query: string
}) {
  const navigate = useNavigate()
  const [hits, setHits] = useState<SearchHit[]>([])
  const [outcome, setOutcome] = useState<SearchOutcome | null>(null)
  const [error, setError] = useState<string | null>(null)
  const buffered = useRef<SearchHit[]>([])

  useEffect(() => {
    const controller = new AbortController()
    setHits([])
    setOutcome(null)
    setError(null)
    buffered.current = []

    // 한 건마다 그리면 500건이 500번의 그리기가 된다. 모아서 이따금 흘려보낸다.
    const flush = () => {
      if (buffered.current.length === 0) return
      const batch = buffered.current
      buffered.current = []
      setHits((current) => [...current, ...batch])
    }
    const timer = setInterval(flush, 120)

    searchEntries(rootId, path, query, (hit) => buffered.current.push(hit), controller.signal)
      .then((result) => {
        flush()
        setOutcome(result)
      })
      .catch((err: unknown) => {
        // 우리가 끊은 것은 실패가 아니다 — 다른 말을 쳤거나 화면을 떠난 것이다.
        if (controller.signal.aborted) return
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => clearInterval(timer))

    return () => {
      controller.abort()
      clearInterval(timer)
    }
  }, [rootId, path, query])

  const where = path === '/' ? '루트' : path
  const running = outcome === null && error === null

  return (
    <>
      {/* 찾는 말이 그대로 들어가는 자리라 조사를 붙이지 않는다 — "사진 를" 이 된다. */}
      <p className="muted small">
        {where} 아래 · 이름에 <strong>“{query}”</strong> · {hits.length}개
        {running && ' · 찾는 중…'}
        {outcome?.stopped === 'limit' && ' · 너무 많아 여기서 멈췄습니다. 말을 더 좁혀보세요.'}
        {outcome?.stopped === 'time' && ' · 시간이 다 되어 여기서 멈췄습니다. 더 안쪽 폴더에서 찾아보세요.'}
      </p>

      {error && <p className="error">찾지 못했습니다 — {error}</p>}

      <table className="entries">
        <thead>
          <tr>
            <th>이름</th>
            <th>자리</th>
            <th className="num">크기</th>
            <th className="num">수정</th>
          </tr>
        </thead>
        <tbody>
          {hits.length === 0 && !running && !error && (
            <tr>
              <td colSpan={4} className="muted center">
                이름에 그 말이 든 것이 없습니다.
              </td>
            </tr>
          )}
          {hits.length === 0 && running && (
            <tr>
              <td colSpan={4} className="muted center">
                찾는 중…
              </td>
            </tr>
          )}
          {hits.map((hit) => (
            <tr key={hit.path}>
              <td>
                <button
                  type="button"
                  className="entry-name"
                  // 폴더는 그 안으로, 파일은 그것이 든 폴더로 간다. 찾은 다음에 할 일은
                  // 대개 그 자리에서 하는 일이라, 목록으로 데려다주는 것이 답이다.
                  onClick={() =>
                    navigate(
                      `/r/${encodeURIComponent(rootId)}${hit.type === 'dir' ? hit.path : parentPath(hit.path)}`,
                    )
                  }
                >
                  {hit.type === 'dir' ? <FolderIcon /> : <FileIcon name={hit.name} />}
                  {hit.name}
                </button>
              </td>
              <td className="mono small muted">{parentPath(hit.path)}</td>
              <td className="num">{hit.type === 'dir' ? '' : formatBytes(hit.size)}</td>
              <td className="num small">{formatDate(hit.mtime)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}
